import { replaceUUID } from '@thesavingsgroup/utils/replaceUUID';
import isEmpty from 'lodash/isEmpty';
import { ReactNode, useEffect, useRef } from 'react';
import { useLocation, useMatches } from 'react-router-dom';

import { RouteChangeAnalyticsParams } from '../../store/analyticStore/types/RouteChangeAnalyticsParams';
import useAnalyticStore from '../../store/analyticStore/useAnalyticStore';
import { getPageKeys } from '../../store/trackingPixelStore/functions/getPageKeys';
import useTrackingPixelStore from '../../store/trackingPixelStore/useTrackingPixelStore';

const RouteChangeGuard = ({ children }: { children: ReactNode }): ReactNode => {
  const location = useLocation();
  const matches = useMatches();
  const { sendLandedWithParametersAnalytics, sendRouteChangeAnalytics } =
    useAnalyticStore();
  const { sendPageView } = useTrackingPixelStore();

  const previousLocation = useRef<string | null>(null);

  useEffect((): void => {
    const fromRouteUrl = previousLocation.current ?? '';
    const fromRouteUrlTrimmed = fromRouteUrl ? replaceUUID(fromRouteUrl) : '';

    const toRouteUrl =
      window.location.origin +
      location.pathname +
      location.search +
      location.hash;
    const toRouteUrlTrimmed = replaceUUID(toRouteUrl);

    // Prevent analytics for non-route changes
    // that could be caused by re-renders or react lifecycle events
    if (fromRouteUrl && fromRouteUrl === toRouteUrl) {
      return;
    }

    const params: RouteChangeAnalyticsParams = {
      fromRouteUrl,
      fromRouteUrlTrimmed,
      toRouteUrl,
      toRouteUrlTrimmed,
    };

    sendRouteChangeAnalytics(params);

    // Fire the tracking pixels configured for this page, matched by its page key and section keys
    sendPageView(getPageKeys(matches));

    // Update the previous location for next route change
    previousLocation.current = toRouteUrl;
  }, [location.key]);

  useEffect((): void => {
    const searchParams = new URLSearchParams(window.location.search);
    const entries = Object.fromEntries(searchParams.entries());

    if (isEmpty(entries)) {
      return;
    }

    sendLandedWithParametersAnalytics(
      window.location.href,
      window.location.host,
      window.location.pathname,
      entries,
    );
  }, []);

  return children;
};

export default RouteChangeGuard;
